import { isEqual, reduce } from "lodash-es";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { loggerService } from "shared/services";
import { IAPIState, StateErrorType } from "shared/types";
import { IUserPermissions, mapUserPermissions } from "shared/utils";
import { SuggestEditForm, SuggestEditFormType } from "Venues/components";
import {
  DeleteInfoBlockModal,
  DeleteStageModal,
  DeleteVenueModal,
  PublishModal,
  RequestPublishModal,
  UnpublishModal,
} from "Venues/containers";

import {
  venueMetaSelector,
  venueSelectorCreator,
  stagesSelectorCreator,
  dispatchGetVenueAction,
  DeleteInfoBlockActionProps,
  dispatchGetAllInfoBlocksAction,
  clearVenueState,
  infoBlocksSelector,
  dataMetaSelector,
} from "Venues/state";
import { IVenueBaseInfoBlocks, IInfoBlockId, IStage, IVenue, VENUE_ACTIONS } from "Venues/types";
import { useVenueEventContext } from "Venues/contexts";

interface VenueContextState {
  venueId: string;
  venue: IVenue | null;
  venueStages: IStage[];
  venueInfoBlocks: IVenueBaseInfoBlocks;
  fetchingVenue: boolean;
  apiMeta: {
    status: IAPIState;
    error?: StateErrorType;
  } | null;
  userPermissions: IUserPermissions;
  fetchingVenueInfoBlocks: boolean;
  brandColor?: string;
  fetchVenue: () => void;
  openDeleteStageModal: (id: string) => void;
  openDeleteVenueModal: () => void;
  openDeleteInfoBlockModal: (infoBlock: DeleteInfoBlockActionProps) => void;
  openPublishModal: () => void;
  openRequestPublishModal: () => void;
  openUnpublishModal: () => void;
  openSuggestEditModal: (props: {
    type: SuggestEditFormType;
    infoBlockSlug?: string;
    stageId?: string;
  }) => void;
}

const initialStage: VenueContextState = {
  venueId: "",
  venue: null,
  venueStages: [],
  apiMeta: null,
  userPermissions: {},
  fetchingVenue: false,
  fetchingVenueInfoBlocks: false,
  venueInfoBlocks: {},
  fetchVenue: () => {},
  openDeleteStageModal: () => {},
  openDeleteVenueModal: () => {},
  openDeleteInfoBlockModal: () => {},
  openPublishModal: () => {},
  openRequestPublishModal: () => {},
  openUnpublishModal: () => {},
  openSuggestEditModal: () => {},
};

const VenuePageContext = createContext(initialStage);

export const VenuePageProvider: React.FC = ({ children }) => {
  const { event, selectedStage, selectedCapacity } = useVenueEventContext();

  const [deleteStage, setDeleteStage] = useState<{
    showModal: boolean;
    stageId?: string;
  }>({
    showModal: false,
  });
  const [showDeleteVenueModal, setShowDeleteVenueModal] = useState(false);
  const [showDeleteInfoBlockModal, setShowDeleteInfoBlockModal] = useState<{
    showModal: boolean;
    infoBlock?: DeleteInfoBlockActionProps;
  }>({ showModal: false });
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showPublishRequestModal, setShowPublishRequestModal] = useState(false);
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [suggestEdit, setSuggestEdit] = useState<{
    showModal: boolean;
    type: SuggestEditFormType;
    infoBlockSlug?: string;
  }>();

  const dispatch = useDispatch();
  const { venueId } = useParams<{
    venueId: string;
  }>();
  const venueIdSelector = useMemo(() => venueSelectorCreator(venueId), [venueId]);
  const venue = useSelector(venueIdSelector);

  const venueStageIds = useMemo(() => {
    return venue?.stages?.map(stage => stage.id);
  }, [venue?.stages]);

  const venueStagesSelector = useMemo(
    () => stagesSelectorCreator(venueStageIds || []),
    [venueStageIds]
  );

  const venueStages = useSelector(venueStagesSelector);
  const filteredVenueStages = useMemo(
    () =>
      event
        ? venueStages
            .filter(({ id }) => id === selectedStage)
            .map(stage => ({
              ...stage,
              stageCapacities: stage.stageCapacities?.filter(({ id }) => id === selectedCapacity),
            }))
        : venueStages,
    [event, selectedCapacity, selectedStage, venueStages]
  );

  const renderCount = useRef<number>(0);
  useEffect(() => {
    renderCount.current = renderCount.current + 1;

    loggerService.log({
      icon: "🔄",
      title: "Renders",
      body: {
        renderCount: renderCount.current,
        venue,
      },
    });
  }, [venue]);

  const { api } = useSelector(venueMetaSelector(venueId));
  const fetchingVenue = useMemo(() => api?.status === IAPIState.pending, [api]);

  const { api: venueInfoBlocksApi } = useSelector(
    dataMetaSelector(VENUE_ACTIONS.getVenueInfoBlocks)
  );
  const fetchingVenueInfoBlocks = useMemo(() => {
    return (
      venueInfoBlocksApi?.status === IAPIState.pending ||
      venueInfoBlocksApi?.status === IAPIState.idle
    );
  }, [venueInfoBlocksApi]);

  const userPermissions = useMemo(
    () => (venue?.permissions ? mapUserPermissions(venue.permissions) : {}),
    [venue?.permissions]
  );

  const brandColor = useMemo(
    () => venue?.brandColor && `#${venue.brandColor}`,
    [venue?.brandColor]
  );

  const fetchVenue = useCallback(
    () => dispatchGetVenueAction(dispatch, venueId),
    [dispatch, venueId]
  );

  const openDeleteStageModal = (stageId: string) => {
    setDeleteStage({
      showModal: true,
      stageId,
    });
  };

  const openDeleteInfoBlockModal = (infoBlock: DeleteInfoBlockActionProps) => {
    setShowDeleteInfoBlockModal({
      showModal: true,
      infoBlock,
    });
  };

  const openSuggestEditModal = (props: { type: SuggestEditFormType }) => {
    setSuggestEdit({ showModal: true, ...props });
  };

  useEffect(() => {
    fetchVenue();
  }, [fetchVenue]);

  const venueInfoBlocks = useSelector(
    infoBlocksSelector(
      reduce(
        venue?.infoBlocks,
        (arr, { infoBlock }) => (infoBlock ? [...arr, infoBlock.data.id] : arr),
        [] as string[]
      )
    )
  );

  // fetch all the venues info blocks
  // do not dispatch if there are no new infoBlocks on the venue
  const infoBlocksRef = useRef<IInfoBlockId[]>();
  useEffect(() => {
    if (venue) {
      if (!isEqual(venue?.infoBlocks, infoBlocksRef.current)) {
        dispatchGetAllInfoBlocksAction(dispatch, venue.infoBlocks, "venue");

        infoBlocksRef.current = venue?.infoBlocks;
      }
    }
  }, [venue, dispatch]);

  // clear venue state on VenuePageContext unmount
  useEffect(() => {
    return () => {
      dispatch(clearVenueState());
    };
  }, [dispatch]);

  return (
    <VenuePageContext.Provider
      value={{
        venueId,
        venue,
        venueStages: filteredVenueStages || [],
        apiMeta: api ?? null,
        userPermissions,
        fetchingVenue,
        venueInfoBlocks,
        fetchingVenueInfoBlocks,
        brandColor,
        fetchVenue,
        openDeleteStageModal,
        openDeleteVenueModal: () => setShowDeleteVenueModal(true),
        openDeleteInfoBlockModal,
        openPublishModal: () => setShowPublishModal(true),
        openRequestPublishModal: () => setShowPublishRequestModal(true),
        openUnpublishModal: () => setShowUnpublishModal(true),
        openSuggestEditModal,
      }}
    >
      {deleteStage.showModal && (
        <DeleteStageModal
          stageId={deleteStage?.stageId || undefined}
          handleClose={() =>
            setDeleteStage({
              showModal: false,
            })
          }
        />
      )}
      {showDeleteVenueModal && (
        <DeleteVenueModal venueId={venueId} handleClose={() => setShowDeleteVenueModal(false)} />
      )}
      {showDeleteInfoBlockModal.showModal && showDeleteInfoBlockModal.infoBlock && (
        <DeleteInfoBlockModal
          infoBlockData={showDeleteInfoBlockModal.infoBlock}
          handleClose={() => setShowDeleteInfoBlockModal({ showModal: false })}
        />
      )}
      {showPublishModal && <PublishModal handleClose={() => setShowPublishModal(false)} />}
      {showPublishRequestModal && (
        <RequestPublishModal
          venueId={venueId}
          handleClose={() => setShowPublishRequestModal(false)}
        />
      )}
      {showUnpublishModal && (
        <UnpublishModal venueId={venueId} handleClose={() => setShowUnpublishModal(false)} />
      )}
      {suggestEdit?.showModal && (
        <SuggestEditForm
          handleClose={() => setSuggestEdit(undefined)}
          type={suggestEdit.type}
          infoBlockSlug={suggestEdit.infoBlockSlug}
        />
      )}
      {children}
    </VenuePageContext.Provider>
  );
};

export const useVenuePageContext = () => useContext(VenuePageContext);
